/**
 * Collection Links — Dawn 테마 호환 버전
 *
 * Horizon 테마의 collection-links.js를 Dawn의 구조에 맞게 재작성.
 * - CustomElement(collection-links-component) 방식 유지
 * - data-ref 속성 기반 참조 (Dawn 방식)
 * - 스포트라이트: 링크 선택 시 이미지 전환
 * - 텍스트: 마우스 호버 시 팝업 이미지 표시
 * - 키보드 접근성 지원 (화살표 키 포커스 이동)
 */

class CollectionLinksComponent extends HTMLElement {
  constructor() {
    super();
    this._rafId = null;
  }

  connectedCallback() {
    this._bindElements();
    this._bindEvents();
  }

  disconnectedCallback() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
  }

  // ── 요소 참조 수집 ──────────────────────────────────────────
  _bindElements() {
    this.container = this.querySelector('[data-ref="container"]');
    this.links = Array.from(this.querySelectorAll('[data-ref="link"]'));
    this.images = Array.from(this.querySelectorAll('[data-ref="image"]'));         // 스포트라이트
    this.hoverImages = Array.from(this.querySelectorAll('[data-ref="hoverImage"]')); // 텍스트
  }

  // ── 이벤트 바인딩 ────────────────────────────────────────────
  _bindEvents() {
    this.links.forEach((link, index) => {
      // 클릭 / 포인터 진입
      link.addEventListener('pointerenter', (e) => this.select(index, e));
      link.addEventListener('focus', () => this.select(index));
    });

    // 키보드 화살표 탐색
    this.addEventListener('keydown', (e) => this._handleKeydown(e));
  }

  // ── 공개 메서드: 선택 처리 ───────────────────────────────────
  /**
   * @param {number} index - 선택할 링크 인덱스
   * @param {PointerEvent} [event]
   */
  select(index, event) {
    this._updateActiveLink(index);

    if (this.dataset.layout === 'spotlight') {
      this._switchSpotlightImage(index);
    }
  }

  // ── 스포트라이트: 활성 링크 표시 ─────────────────────────────
  _updateActiveLink(activeIndex) {
    this.links.forEach((link, i) => {
      link.setAttribute('aria-current', String(i === activeIndex));
    });
  }

  // ── 스포트라이트: 이미지 전환 ─────────────────────────────────
  _switchSpotlightImage(index) {
    this.images.forEach((img, i) => {
      img.classList.toggle('is-active', i === index);
      img.setAttribute('aria-hidden', String(i !== index));
    });
  }

  // ── 텍스트: 호버 이미지 표시 ─────────────────────────────────
  _showHoverImage(event, index) {
    // 다른 이미지 숨기기
    this.hoverImages.forEach((img, i) => {
      if (i !== index) {
        img.removeAttribute('data-visible');
        img.setAttribute('hidden', '');
      }
    });

    const target = this.hoverImages[index];
    if (!target) return;

    target.removeAttribute('hidden');
    requestAnimationFrame(() => {
      target.setAttribute('data-visible', '');
    });

    this._positionHoverImage(event, target);
  }

  _hideHoverImage(index) {
    const target = this.hoverImages[index];
    if (!target) return;
    target.removeAttribute('data-visible');
    setTimeout(() => {
      if (!target.hasAttribute('data-visible')) {
        target.setAttribute('hidden', '');
      }
    }, 220);
  }

  _updateHoverImagePos(event, index) {
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      const target = this.hoverImages[index];
      if (target) this._positionHoverImage(event, target);
    });
  }

  /**
   * 팝업 이미지가 뷰포트를 벗어나지 않도록 위치 계산
   * @param {MouseEvent} event
   * @param {HTMLElement} imageEl
   */
  _positionHoverImage(event, imageEl) {
    const offset = 16;
    const imgW = imageEl.offsetWidth || 240;
    const imgH = imageEl.offsetHeight || 240;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = event.clientX + offset;
    let y = event.clientY + offset;

    // 오른쪽 잘림 방지
    if (x + imgW > vw - offset) x = event.clientX - imgW - offset;
    // 아래쪽 잘림 방지
    if (y + imgH > vh - offset) y = event.clientY - imgH - offset;

    x = Math.max(offset, x);
    y = Math.max(offset, y);

    imageEl.style.setProperty('--cl-x', `${x}px`);
    imageEl.style.setProperty('--cl-y', `${y}px`);
  }

  // ── 키보드 접근성 ─────────────────────────────────────────────
  _handleKeydown(event) {
    let modifier = 0;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const current = this.links.findIndex((l) => l === document.activeElement);
    const next = (current + modifier + this.links.length) % this.links.length;
    this.links[next]?.focus();
  }
}

if (!customElements.get('collection-links-component')) {
  customElements.define('collection-links-component', CollectionLinksComponent);
}
